/**
 * Viewport and fetch-area math. Pure: no Mapbox, no DOM, unit-tested.
 *
 * ONE FETCH AREA FOR EVERY FEED (the recon map's "one coverage radius" rule):
 * the viewport padded by 25% on each side, snapped OUTWARD to a zoom-based
 * grid. Panning inside it changes nothing — not the area, not the query key —
 * so nothing refetches until the view actually leaves what was fetched.
 */

export interface GeoBounds {
  minLat: number
  minLng: number
  maxLat: number
  maxLng: number
}

export const LISBON = { lat: 38.7223, lng: -9.1393 } as const

export const EARTH_RADIUS_M = 6_371_008.8

/** Fraction of the viewport's width/height added on EACH side. */
export const FETCH_PAD = 0.25

/** Zoom levels: below this nothing street-level is fetched or drawn. */
export const STREET_MIN_ZOOM = 14

/**
 * A fetch area more than this many times the (padded) viewport's area is too
 * coarse — we zoomed in a long way inside it — and is re-derived, so the
 * server's limit and clamp apply to what is actually on screen.
 */
export const MAX_AREA_RATIO = 6

const toRad = (d: number) => (d * Math.PI) / 180

export function haversineMeters(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const dLat = toRad(bLat - aLat)
  const dLng = toRad(bLng - aLng)
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)))
}

export function clampLat(lat: number): number {
  return Math.max(-85, Math.min(85, lat))
}

export function center(b: GeoBounds): { lat: number; lng: number } {
  return { lat: (b.minLat + b.maxLat) / 2, lng: (b.minLng + b.maxLng) / 2 }
}

/** Distance from the centre to a corner: the radius of a disc covering the box. */
export function coverRadiusMeters(b: GeoBounds): number {
  const c = center(b)
  return haversineMeters(c.lat, c.lng, b.maxLat, b.maxLng)
}

export function padBounds(b: GeoBounds, frac: number = FETCH_PAD): GeoBounds {
  const dLat = (b.maxLat - b.minLat) * frac
  const dLng = (b.maxLng - b.minLng) * frac
  return {
    minLat: clampLat(b.minLat - dLat),
    maxLat: clampLat(b.maxLat + dLat),
    minLng: Math.max(-180, b.minLng - dLng),
    maxLng: Math.min(180, b.maxLng + dLng),
  }
}

export function containsBounds(outer: GeoBounds, inner: GeoBounds): boolean {
  return (
    inner.minLat >= outer.minLat &&
    inner.maxLat <= outer.maxLat &&
    inner.minLng >= outer.minLng &&
    inner.maxLng <= outer.maxLng
  )
}

export function containsPoint(b: GeoBounds, lat: number, lng: number): boolean {
  return lat >= b.minLat && lat <= b.maxLat && lng >= b.minLng && lng <= b.maxLng
}

export function areaDeg2(b: GeoBounds): number {
  return Math.max(0, b.maxLat - b.minLat) * Math.max(0, b.maxLng - b.minLng)
}

/** Grid step in degrees for a zoom: a quarter of a 512 px tile's width. */
export function gridStep(zoom: number): number {
  return 360 / 2 ** (Math.floor(zoom) + 2)
}

/** Snaps every edge outward to the grid, so nearby viewports share one area. */
export function quantizeOutward(b: GeoBounds, step: number): GeoBounds {
  const r = (v: number) => Math.round(v * 1e9) / 1e9
  return {
    minLat: clampLat(r(Math.floor(b.minLat / step) * step)),
    minLng: Math.max(-180, r(Math.floor(b.minLng / step) * step)),
    maxLat: clampLat(r(Math.ceil(b.maxLat / step) * step)),
    maxLng: Math.min(180, r(Math.ceil(b.maxLng / step) * step)),
  }
}

/**
 * The fetch area for a viewport. Returns `prev` UNCHANGED (same object) when
 * the viewport is still inside it and it is not grossly oversized, so the
 * query key does not move.
 */
export function nextFetchArea(viewport: GeoBounds, zoom: number, prev: GeoBounds | null): GeoBounds {
  const padded = padBounds(viewport)
  if (prev && containsBounds(prev, viewport) && areaDeg2(prev) <= MAX_AREA_RATIO * areaDeg2(padded)) {
    return prev
  }
  return quantizeOutward(padded, gridStep(zoom))
}

/** A stable string for query keys (6 dp ≈ 0.1 m). */
export function boundsKey(b: GeoBounds): string {
  return [b.minLat, b.minLng, b.maxLat, b.maxLng].map((v) => v.toFixed(6)).join(',')
}

/** The heat bounds are clamped server-side to ≤ 1° per axis around the centre. */
export function clampSpan(b: GeoBounds, maxSpanDeg: number): GeoBounds {
  const c = center(b)
  const halfLat = Math.min((b.maxLat - b.minLat) / 2, maxSpanDeg / 2)
  const halfLng = Math.min((b.maxLng - b.minLng) / 2, maxSpanDeg / 2)
  return { minLat: c.lat - halfLat, maxLat: c.lat + halfLat, minLng: c.lng - halfLng, maxLng: c.lng + halfLng }
}

/** Mercator pixel radius of `meters` at a zoom (512 px tiles, as Mapbox GL uses). */
export function metersToPixels(meters: number, lat: number, zoom: number): number {
  const metersPerPixel = (2 * Math.PI * 6_378_137 * Math.cos(toRad(lat))) / (512 * 2 ** zoom)
  return meters / metersPerPixel
}
